<script setup>
import {onMounted, onUnmounted, reactive, watch} from "vue";
import mapboxgl from 'mapbox-gl';
import bluemapmarker from './bluemapmarker-removebg-preview.png';
import greenmapmarker from './greenmapmarker-removebg-preview.png';
import redmapmarker from './redmapmarker-removebg-preview.png';
import yellowmapmarker from './yellowmapmarker-removebg-preview.png';
import countdown from '@/StandaloneJS/vue-countdown';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {ToastUtil} from "@/Utils/ToastUtil";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";

const compName = 'TasksPendingAdjuster';

const props = defineProps({
  clientId: null
});

const data = reactive(
    {
      search: null,
      dataLoading: true,
      newAdj: false,
      map: null,
      popup: '',
      apps: {
        type: 'FeatureCollection',
        crs: {
          type: 'name',
          properties: { name: 'urn:ogc:def:crs:OGC:1.3:CRS84' },
        },
        features: [],
      },
      appscomplete: {
        type: 'FeatureCollection',
        crs: {
          type: 'name',
          properties: { name: 'urn:ogc:def:crs:OGC:1.3:CRS84' },
        },
        features: [],
      },
      appsOther: {
        type: 'FeatureCollection',
        crs: {
          type: 'name',
          properties: { name: 'urn:ogc:def:crs:OGC:1.3:CRS84' },
        },
        features: [],
      },
      appsRecontact: {
        type: 'FeatureCollection',
        crs: {
          type: 'name',
          properties: { name: 'urn:ogc:def:crs:OGC:1.3:CRS84' },
        },
        features: [],
      },
      headers: [
        {title: 'Claim Number', key: 'claimnumber'},
        {title: 'Insured', key: 'insuredname'},
        {title: 'Client', key: 'clientsname'},
        {title: 'Claim Age (Days)', key: 'age'},
        {title: 'Pending Task', key: 'name'},
        {title: 'Appointment', key: 'apptimestamp'},
        {title: 'Time Remaining', key: 'timedue'},
      ],
      items: [],
    }
);

watch(() => props.clientId, async (val) => {
  getData();
});

const formatTimestampByLocale = (timestamp) => {
  const date = new Date(timestamp)
  const timeOptions = {
    hour12: true,
    hour: "numeric",
    minute: "2-digit",
  }
  const dateOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZoneName: "short"
  }
  return date.toLocaleString('en', { ...dateOptions, ...timeOptions })
}

const getData = () => {
  data.dataLoading = true;

  ApiWrapper.post('tasks-pending','list-tasks-pending-adjuster',{
    clientId: props.clientId
  })
      .then((response) => {
        data.apps.features = [];
        data.appscomplete.features = [];
        data.appsOther.features = [];
        data.appsRecontact.features = [];

        for (let x in response.data.rows) {
          if (response.data.rows[x].apptimestamp != null) {
            let d = new Date(response.data.rows[x].apptimestamp)
            // *** Old code without timezone displaying
            // response.data.rows[x].apptimestamp = d.toLocaleString()
            response.data.rows[x].apptimestamp = formatTimestampByLocale(d)
          } else {
            response.data.rows[x].apptimestamp = 'Not Set'
          }
        }

        data.items = response.data.rows;
        for (let x in response.data.geodata.features) {
          if (response.data.geodata.features[x].properties.apptimestamp != null) {
            let d = new Date(response.data.geodata.features[x].properties.apptimestamp)
            response.data.geodata.features[x].properties.apptimestamp = d.toLocaleString()
          } else {
            response.data.geodata.features[x].properties.apptimestamp = 'Not Set'
          }
          if (response.data.geodata.features[x].properties.name === "Make Contact") {
            data.apps.features.push(response.data.geodata.features[x])
          } else if (response.data.geodata.features[x].properties.name === 'Complete Inspection') {
            data.appscomplete.features.push(response.data.geodata.features[x])
          } else if (response.data.geodata.features[x].properties.name === "Recontact") {
            data.appsRecontact.features.push(response.data.geodata.features[x])
          } else {
            data.appsOther.features.push(response.data.geodata.features[x])
          }
        }

        createMapMarkers();
        data.dataLoading = false;
      })
      .catch((error) => {
        ToastUtil.error(error);
        data.dataLoading = false;
      })
}

const clearMarkers = () => {
  if (typeof data.map.getLayer('claim') !== 'undefined') {
    data.map.removeLayer('claim');
  }
  if (typeof data.map.getLayer('claimRecontact') !== 'undefined') {
    data.map.removeLayer('claimRecontact');
  }
  if (typeof data.map.getLayer('claimOther') !== 'undefined') {
    data.map.removeLayer('claimOther');
  }
  if (typeof data.map.getLayer('claimcomplete') !== 'undefined') {
    data.map.removeLayer('claimcomplete');
  }

  if (typeof data.map.getSource('apps') !== 'undefined') {
    data.map.removeSource('apps');
  }
  if (typeof data.map.getSource('appsRecontact') !== 'undefined') {
    data.map.removeSource('appsRecontact');
  }
  if (typeof data.map.getSource('appsOther') !== 'undefined') {
    data.map.removeSource('appsOther');
  }
  if (typeof data.map.getSource('appscomplete') !== 'undefined') {
    data.map.removeSource('appscomplete');
  }
}

const createMapMarkers = () => {
  clearMarkers();

  data.map.addSource('apps', {
    type: 'geojson',
    data: data.apps,
    cluster: false,
  })
  data.map.addLayer({
    id: 'claim',
    source: 'apps',
    type: 'symbol',
    layout: {
      'icon-image': 'makecontact',
      'icon-size': 0.10,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
    },
  })

  data.map.addSource('appsRecontact', {
    type: 'geojson',
    data: data.appsRecontact,
    cluster: false,
  })
  data.map.addLayer({
    id: 'claimRecontact',
    source: 'appsRecontact',
    type: 'symbol',
    layout: {
      'icon-image': 'recontact',
      'icon-size': 0.10,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
    },
  })
  data.map.addSource('appsOther', {
    type: 'geojson',
    data: data.appsOther,
    cluster: false,
  })
  data.map.addLayer({
    id: 'claimOther',
    source: 'appsOther',
    type: 'symbol',
    layout: {
      'icon-image': 'othertask',
      'icon-size': 0.10,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
    },
  })

  data.map.addSource('appscomplete', {
    type: 'geojson',
    data: data.appscomplete,
    cluster: false,
  })

  data.map.addLayer({
    id: 'claimcomplete',
    source: 'appscomplete',
    type: 'symbol',
    layout: {
      'icon-image': 'completeinspection',
      'icon-size': 0.10,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
    },
  })

  data.map.on('click', 'claim', function (e) {
    if (data.popup != '') {
      data.popup.remove()
    }
    let stackedApps = []

    for (let x in e.features) {
      let newApp = {
        claimnumber: e.features[x].properties.claimnumber,
        claimid: e.features[x].properties.claimid,
        name: e.features[x].properties.name,
        insuredname: e.features[x].properties.insuredname,
        age: e.features[x].properties.age,
        clientname: e.features[x].properties.clientname,
        clientid: e.features[x].properties.clientid,
        apptimestamp: e.features[x].properties.apptimestamp,
      }
      stackedApps.push(newApp)
    }
    let htmlString = ''
    for (let x in stackedApps) {
      htmlString +=
          `<div style="width: 175px; padding: 5px;">


                    <h3><a href="` +
          ApiWrapper.localPath +
          `/claim/` +
          stackedApps[x].claimid +
          `" target="_blank"> Claim: ` +
          stackedApps[x].claimnumber +
          `</a></h3>

                    <h3> Client: ` +
          stackedApps[x].clientname +
          `</h3>


                    <h3> Insured: ` +
          stackedApps[x].insuredname +
          `</h3>

                    <h3> Age: ` +
          stackedApps[x].age +
          `</h3>

                    <h3> Current Task: ` +
          stackedApps[x].name +
          `</h3>

                    <h3> Appointment Time: ` +
          stackedApps[x].apptimestamp +
          `</h3>

                </div>`
    }
    let coordinates = e.features[0].geometry.coordinates.slice()
    // Ensure that if thethis.map is zoomed out such that
    // multiple copies of the feature are visible, the
    // popup appears over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
    }

    data.popup = new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(htmlString)
        .addTo(data.map)
  })


  data.map.on('click', 'claimRecontact', function (e) {
    if (data.popup != '') {
      data.popup.remove()
    }
    let stackedApps = []

    for (let x in e.features) {
      let newApp = {
        claimnumber: e.features[x].properties.claimnumber,
        claimid: e.features[x].properties.claimid,
        name: e.features[x].properties.name,
        insuredname: e.features[x].properties.insuredname,
        age: e.features[x].properties.age,
        clientname: e.features[x].properties.clientname,
        clientid: e.features[x].properties.clientid,
        apptimestamp: e.features[x].properties.apptimestamp,
      }
      stackedApps.push(newApp)
    }
    let htmlString = ''
    for (let x in stackedApps) {
      htmlString +=
          `<div style="width: 175px; padding: 5px;">


                    <h3><a href="` +
          ApiWrapper.localPath +
          `/claim/` +
          stackedApps[x].claimid +
          `" target="_blank"> Claim: ` +
          stackedApps[x].claimnumber +
          `</a></h3>


                    <h3> Client: ` +
          stackedApps[x].clientname +
          `</h3>


                    <h3> Insured: ` +
          stackedApps[x].insuredname +
          `</h3>

                    <h3> Age: ` +
          stackedApps[x].age +
          `</h3>

                    <h3> Current Task: ` +
          stackedApps[x].name +
          `</h3>

                    <h3> Appointment Time: ` +
          stackedApps[x].apptimestamp +
          `</h3>

                </div>`
    }
    let coordinates = e.features[0].geometry.coordinates.slice()
    // Ensure that if thethis.map is zoomed out such that
    // multiple copies of the feature are visible, the
    // popup appears over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
    }

    data.popup = new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(htmlString)
        .addTo(data.map)
  })

  data.map.on('click', 'claimOther', function (e) {
    if (data.popup != '') {
      data.popup.remove()
    }
    let stackedApps = []

    for (let x in e.features) {
      let newApp = {
        claimnumber: e.features[x].properties.claimnumber,
        claimid: e.features[x].properties.claimid,
        name: e.features[x].properties.name,
        insuredname: e.features[x].properties.insuredname,
        age: e.features[x].properties.age,
        clientname: e.features[x].properties.clientname,
        clientid: e.features[x].properties.clientid,
        apptimestamp: e.features[x].properties.apptimestamp,
      }
      stackedApps.push(newApp)
    }
    let htmlString = ''
    for (let x in stackedApps) {
      htmlString +=
          `<div style="width: 175px; padding: 5px;">


                    <h3><a href="` +
          ApiWrapper.localPath +
          `/claim/` +
          stackedApps[x].claimid +
          `" target="_blank"> Claim: ` +
          stackedApps[x].claimnumber +
          `</a></h3>


                    <h3> Client: ` +
          stackedApps[x].clientname +
          `</h3>


                    <h3> Insured: ` +
          stackedApps[x].insuredname +
          `</h3>

                    <h3> Age: ` +
          stackedApps[x].age +
          `</h3>

                    <h3> Current Task: ` +
          stackedApps[x].name +
          `</h3>

                    <h3> Appointment Time: ` +
          stackedApps[x].apptimestamp +
          `</h3>

                </div>`
    }
    let coordinates = e.features[0].geometry.coordinates.slice()
    // Ensure that if thethis.map is zoomed out such that
    // multiple copies of the feature are visible, the
    // popup appears over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
    }

    data.popup = new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(htmlString)
        .addTo(data.map)
  })


  data.map.on('click', 'claimcomplete', function (e) {
    if (data.popup != '') {
      data.popup.remove()
    }
    let stackedApps = []

    for (let x in e.features) {
      let newApp = {
        claimnumber: e.features[x].properties.claimnumber,
        claimid: e.features[x].properties.claimid,
        name: e.features[x].properties.name,
        insuredname: e.features[x].properties.insuredname,
        age: e.features[x].properties.age,
        clientname: e.features[x].properties.clientname,
        clientid: e.features[x].properties.clientid,
        apptimestamp: e.features[x].properties.apptimestamp,

      }
      stackedApps.push(newApp)
    }
    let htmlString = ''
    for (let x in stackedApps) {
      htmlString +=
          `<div style="width: 175px; padding: 5px;">


                    <h3><a href="` +
          ApiWrapper.localPath +
          `/claim/` +
          stackedApps[x].claimid +
          `" target="_blank"> Claim: ` +
          stackedApps[x].claimnumber +
          `</a></h3>


                    <h3> Client: ` +
          stackedApps[x].clientname +
          `</h3>


                    <h3> Insured: ` +
          stackedApps[x].insuredname +
          `</h3>

                    <h3> Age: ` +
          stackedApps[x].age +
          `</h3>

                    <h3> Current Task: ` +
          stackedApps[x].name +
          `</h3>


                    <h3> Appointment Time: ` +
          stackedApps[x].apptimestamp +
          `</h3>

                </div>`
    }
    let coordinates = e.features[0].geometry.coordinates.slice()
    // Ensure that if thethis.map is zoomed out such that
    // multiple copies of the feature are visible, the
    // popup appears over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
    }

    data.popup = new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(htmlString)
        .addTo(data.map)
  })

}

onMounted(() => {
  loadMap();

  activeComponentStore.registerComponent(
      compName,
      [
        'tasks'
      ],
      loadMap
  );
});

onUnmounted(() => {
  activeComponentStore.deregisterComponent(compName);
});

const loadMap = () => {
  //TODO: transfer this access token to AWS Secrets Manager
  mapboxgl.accessToken =
      'pk.eyJ1IjoiZW50cmVvcyIsImEiOiJja2RydG16MXEwZ3hvMnhuOXVlcHZhcWRvIn0.mlZA3MAQSFupaQbmFjA9QQ'

  data.map = new mapboxgl.Map({
    container: 'map',
    style: 'mapbox://styles/mapbox/streets-v11',
    center: [-99.121313, 39.816975 ],
    zoom: 3,
  })

  data.map.on('load', function () {
    data.map.loadImage(redmapmarker, function (error, image) {
      if (error) throw error
      data.map.addImage('makecontact', image)
    })
    data.map.loadImage(yellowmapmarker, function (error, image) {
      if (error) throw error
      data.map.addImage('recontact', image)
    })
    data.map.loadImage(greenmapmarker, function (error, image) {
      if (error) throw error
      data.map.addImage('completeinspection', image)
    })
    data.map.loadImage(bluemapmarker, function (error, image) {
      if (error) throw error
      data.map.addImage('othertask', image)
    })
    getData()
  })
}

</script>

<template>
  <v-container  fluid>
    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Pending Tasks On Your Claims</v-toolbar-title>
        <v-divider class="mx-4" inset vertical></v-divider>
      </v-toolbar>
      <v-row>
        <v-col cols="4">
          <v-text-field v-model="data.search" append-icon="mdi-magnify" label="Search" single-line hide-details
                        class="ml-4"></v-text-field>
        </v-col>
        <v-col>
          <v-btn variant="plain" class="mt-1" icon @click="getData()">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-data-table :items="data.items" :headers="data.headers" item-value="tasksid" :search="data.search"
                    :loading="data.dataLoading" loading-text="Loading... Please wait"               :items-per-page-options="[100, 10, 15, 30, 50]"            >
        <template v-slot:[`item.claimnumber`]="{ item }">
          <RouterLink :to="`/claim/${item.claimid}`">
            {{item.claimnumber}}
          </RouterLink>
        </template>
        <template v-slot:[`item.personname`]="{ item }">
          <RouterLink :to="`/userprofile/${item.personid}`">
            {{ item.personname }}
          </RouterLink>
        </template>

        <template v-slot:[`item.timedue`]="{item}">
          <v-chip label v-if="new Date(item.timedue) - new Date() > 0" class="success text--white">
            <v-icon dark left>mdi-calendar</v-icon>
            <countdown :time="new Date(item.timedue) - new Date()">
              <template v-slot="props">
                {{ props.totalHours.toString().padStart(2,0) }}:{{
                  props.minutes.toString().padStart(2,0) }}:{{
                  props.seconds.toString().padStart(2,0) }}
              </template>
            </countdown>
          </v-chip>
          <v-chip label v-else-if="!item.timecompleted" class="error text--white">
            <v-icon dark left>mdi-calendar</v-icon>
            <countdown :time="new Date() - new Date(item.timedue)" :interval="-1000">
              <template v-slot="props">
                {{ props.totalHours.toString().padStart(2,0) }}:{{
                  props.minutes.toString().padStart(2,0) }}:{{
                  props.seconds.toString().padStart(2,0) }}
              </template>
            </countdown>
          </v-chip>
        </template>
      </v-data-table>
      <v-row>
        <span class="pl-5">Make Contact: </span>
        <v-icon color="red">
          mdi-map-marker-question
        </v-icon>

        <span class="pl-5">Recontact: </span>
        <v-icon color="yellow">
          mdi-map-marker-question
        </v-icon>

        <span class="pl-5">Other: </span>
        <v-icon color="blue">
          mdi-map-marker-question
        </v-icon>

        <span class="pl-5">Complete Inspection: </span>
        <v-icon color="green">
          mdi-map-marker-question
        </v-icon>
      </v-row>
      <v-row align="center">
        <v-col align="center" cols="12">
          <div id="map"></div>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<style scoped>
v-container {
  padding: 5px;
}

[v-cloak] {
  display: none;
}

.mapboxgl-map {
  width: 100%;
  height: 600px;
}
#map {
  top: 0;
  bottom: 0;
}
.marker {
  background-image: url('./mapbox-A.png');
  background-size: cover;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
}
.mapboxgl-popup-content {
  font-family: 'Oswald';
  box-shadow: 0 3px 14px rgba(0, 0, 0, 0.4);
}
.mapboxgl-popup-close-button {
  padding-right: 5px;
}
.mapboxgl-popup-content-wrapper {
  border-radius: 12px;
}

.transparent {
  background: rgba(0, 0, 0, 0);
}
</style>